import { useParams, Link } from 'react-router-dom';
import { tvShows } from '@/mocks/tv-shows';
import { PageMeta, JsonLd } from '@/components/feature/SeoComponents';
import TvShowCard from '@/pages/home/components/TvShowCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const show = tvShows.find(s => s.id === Number(id));

  if (!show) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">未找到该剧集</h1>
          <p className="text-foreground-500 mb-6">可能已被下架或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedShows = tvShows
    .filter(s => s.id !== show.id && (s.categorySlug === show.categorySlug || s.tags.some(t => show.tags.includes(t))))
    .slice(0, 6);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${show.title} - 老子影视网`,
    description: show.description,
    author: {
      '@type': 'Organization',
      name: '老子影视网',
    },
    datePublished: `${show.year}-01-01`,
    publisher: {
      '@type': 'Organization',
      name: '老子影视网',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.laoziys.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: show.category,
          item: `https://www.laoziys.com/category/${show.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: show.title,
          item: `https://www.laoziys.com/article/${show.id}`,
        },
      ],
    },
  };

  return (
    <>
      <PageMeta
        title={`${show.title} | ${show.category}全集在线播放 | 老子影视网`}
        description={`${show.title}全集免费在线观看，${show.description.slice(0, 100)}。老子影视网高清不缓冲，追剧必备。`}
        keywords={`${show.title},${show.category},${show.tags.join(',')},免费观看,高清,老子影视网`}
        canonicalUrl={`https://www.laoziys.com/article/${show.id}`}
      />
      <JsonLd data={articleJsonLd} />

      <main className="pt-20">
        <nav className="w-full px-4 md:px-6 py-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${show.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{show.category}</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium">{show.title}</li>
          </ol>
        </nav>

        <section className="w-full px-4 md:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="lg:w-[300px] flex-shrink-0">
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-6">
                <img
                  src={show.imageUrl}
                  alt={show.title}
                  title={`${show.title} - 老子影视网${show.category}全集在线播放`}
                  className="w-full h-full object-cover object-top"
                />
                {show.isNew && (
                  <span className="absolute top-3 left-3 px-3 py-1 bg-accent-500 text-white text-xs font-medium rounded-md">
                    NEW
                  </span>
                )}
                {show.isHot && (
                  <span className="absolute top-3 right-3 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-md">
                    热播
                  </span>
                )}
              </div>

              <button className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-base transition-all duration-200 whitespace-nowrap cursor-pointer mb-4">
                <i className="ri-play-circle-line text-xl"></i>
                立即播放
              </button>

              <div className="bg-background-100 rounded-xl p-4 border border-background-200/70">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">剧集信息</h3>
                <dl className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">导演</dt>
                    <dd className="text-foreground-800 font-medium">{show.director}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">主演</dt>
                    <dd className="text-foreground-800 font-medium text-right">{show.cast.join('、')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">类型</dt>
                    <dd className="text-foreground-800 font-medium">{show.tags.join(' / ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">地区</dt>
                    <dd className="text-foreground-800 font-medium">{show.region}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">年份</dt>
                    <dd className="text-foreground-800 font-medium">{show.year}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">集数</dt>
                    <dd className="text-foreground-800 font-medium">{show.episodes}集</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">状态</dt>
                    <dd className="text-primary-600 font-medium">{show.status}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-500">评分</dt>
                    <dd className="text-accent-500 font-bold">
                      <i className="ri-star-fill text-xs mr-0.5"></i>{show.rating}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2">
                {show.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-md">
                  {show.category}
                </span>
                <span className="text-sm text-foreground-500">{show.year}年</span>
                <span className="text-sm text-foreground-500">|</span>
                <span className="text-sm text-accent-500 font-medium">
                  <i className="ri-star-fill text-xs mr-0.5"></i>{show.rating} 分
                </span>
                <span className="text-sm text-foreground-500">|</span>
                <span className="text-sm text-primary-600 font-medium">{show.status}</span>
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70 mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {show.description}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  选集 <span className="text-sm font-normal text-foreground-500">({show.updateInfo})</span>
                </h2>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: Math.min(show.episodes, 24) }, (_, i) => (
                    <button
                      key={i}
                      className="w-12 h-9 flex items-center justify-center rounded-md bg-background-100 hover:bg-primary-500 hover:text-white text-sm font-medium text-foreground-700 transition-all duration-200 cursor-pointer border border-background-200/70"
                    >
                      {i + 1}
                    </button>
                  ))}
                  {show.episodes > 24 && (
                    <button className="w-12 h-9 flex items-center justify-center rounded-md bg-background-100 text-sm font-medium text-foreground-500 cursor-pointer border border-background-200/70">
                      ...
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-500">标签:</span>
                {show.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                为你推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">根据你的喜好，猜你还想看</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedShows.map((s) => (
              <TvShowCard key={s.id} show={s} />
            ))}
          </div>
        </section>
      </main>
    </>
  );
}